import { memoize } from 'lodash';
import NamedRegExp from 'named-regexp-groups';

export interface TokenBase<T extends string, D = never> {
  type: T;
  data?: D;
}

export type NewLineToken = TokenBase<'newline'>;
export type CharacterToken = TokenBase<'character', string>;
export type VariableToken = TokenBase<
  'variable',
  {
    variableName: string;
    defaultValue?: string;
  }
>;
export type EofToken = TokenBase<'eof'>;

export type Token = NewLineToken | CharacterToken | VariableToken | EofToken;

function createToken(type: 'eof'): EofToken;
function createToken(type: 'newline'): NewLineToken;
function createToken(
  type: 'character',
  data: CharacterToken['data']
): CharacterToken;
function createToken(
  type: 'variable',
  data: VariableToken['data']
): VariableToken;
function createToken<Type extends Token['type']>(
  type: Type,
  data?: unknown
): Token {
  return { type, data } as Token;
}

export type ExecArray = Required<
  RegExpExecArray | Exclude<null, ReturnType<NamedRegExp['exec']>>
>;

export interface TokenTypeObject<TokenType extends Token> {
  pattern: RegExp | NamedRegExp;
  createToken: (match: ExecArray) => TokenType;
}

export interface TokenTypes {
  newline: TokenTypeObject<NewLineToken>;
  character: TokenTypeObject<CharacterToken>;
  variable: TokenTypeObject<VariableToken>;
}

const TokenType: TokenTypes = {
  newline: {
    pattern: /^\n/,
    createToken: () => createToken('newline'),
  },

  character: {
    pattern: new NamedRegExp(/^(:<value>.)/),
    createToken: (match) => createToken('character', match?.groups?.value),
  },

  variable: {
    pattern: new NamedRegExp(
      /^{(:<variableName>[a-zA-Z_]+)([|](:<defaultValue>(.*)))?}/
    ),
    createToken: (match) =>
      createToken('variable', {
        variableName: match.groups.variableName,
        defaultValue: match.groups.defaultValue,
      }),
  },
};

function test<TokenType extends Token>(
  tokenType: TokenTypeObject<TokenType>,
  input: string
) {
  return tokenType.pattern.test(input);
}

function getToken(input: string): [string, Token | null] {
  if (test(TokenType.variable, input)) {
    return take(input, TokenType.variable);
  }

  if (test(TokenType.newline, input)) {
    return take(input, TokenType.newline);
  }

  if (test(TokenType.character, input)) {
    return take(input, TokenType.character);
  }

  return [input.slice(1), null];
}

function take<TokenType extends Token>(
  input: string,
  tokenType: TokenTypeObject<TokenType>
): [string, TokenType | null] {
  const { pattern: re, createToken } = tokenType;
  const match = re.exec(input);

  if (match != null) {
    return [
      input.replace(re, ''),
      createToken(match as Required<RegExpExecArray>),
    ];
  }

  return [input, null];
}

export default memoize((text: string) => {
  let input = text || '';
  let token: Token | null = null;
  const tokens = new Array<Token>();

  while (input.length > 0) {
    [input, token] = getToken(input);

    if (token != null) {
      tokens.push(token);
    }

    if (input.length === 0) {
      tokens.push(createToken('eof'));
    }
  }

  return tokens;
});
